export type SolutionComponent = 'SKP' | 'Human Capability Framework' | 'Noetivra'

export const solutionScenarios = [
  {
    setting: 'A research institution',
    need: 'may need to discover relationships across thousands of papers.',
  },
  {
    setting: 'A school',
    need: 'may need better pathways for developing reasoning.',
  },
  {
    setting: 'A public institution',
    need: 'may need to connect policy, staff development, and reviewable decisions.',
  },
]

export const solutionSettings: {
  id: string
  setting: string
  tagline: string
  components: SolutionComponent[]
  planned?: boolean
  link: string
  linkLabel: string
  paragraphs: string[]
  safeguard?: string
  applications: string[]
}[] = [
  {
    id: 'scientific-research',
    setting: 'For scientific research',
    tagline: 'Connect ideas across disciplines and documents.',
    components: ['SKP', 'Noetivra'],
    link: '/semantic-knowledge',
    linkLabel: 'Explore SKP',
    paragraphs: [
      'SKP can help research organizations organize papers, people, concepts, methods, populations, and findings in a knowledge graph. AI proposes entities and relationships from source material; approved vocabularies, validation rules, and human reviewers govern what becomes part of the trusted knowledge base.',
      'Researchers could search by concept and relationship, follow a finding to its source passage, compare related studies, and discover connections that keyword searches may miss. Semantic search and GraphRAG make that knowledge easier to explore while keeping its origins visible. Noetivra Research, a planned module, would add interactive knowledge maps and shared review.',
    ],
    applications: [
      'Literature discovery',
      'Biomedical knowledge graphs',
      'Research portals',
      'Evidence mapping',
      'Cross-disciplinary collaboration',
    ],
  },
  {
    id: 'education',
    setting: 'For education',
    tagline: 'Build reasoning and self-direction over time.',
    components: ['Human Capability Framework', 'SKP', 'Noetivra'],
    link: '/education',
    linkLabel: 'Education in depth',
    paragraphs: [
      'CJI and NCJI research can inform learning activities focused on evaluating claims, considering consequences, planning, cooperation, and resilience to manipulation. SKP can connect each activity to its capability definition, instructional material, and research basis.',
      'At early stages, the emphasis is on support and growth. Through Noetivra, learners could complete tasks, explore feedback, and follow development activities, while programs examine which activities help and revise them accordingly.',
    ],
    applications: [
      'Reasoning curricula',
      'Media literacy',
      'Mentoring pathways',
      'Longitudinal development pilots',
    ],
  },
  {
    id: 'employers',
    setting: 'For employers and workforce programs',
    tagline: 'Connect strengths to development and opportunity.',
    components: ['Human Capability Framework', 'SKP', 'Noetivra'],
    link: '/employers',
    linkLabel: 'Employers in depth',
    paragraphs: [
      'Organizations can use the Human Capability Framework to explore decision quality, planning, communication, and leadership development. Findings can lead to practice, coaching, or learning opportunities, with follow-up to see whether they helped.',
      'SKP can maintain a consistent language for skills and role requirements across teams and programs. Noetivra Talent would support structured interviews and candidate review, with human reviewers making hiring decisions. Opportunity matching can explain why a path may fit a person’s demonstrated strengths while keeping guidance separate from a consequential eligibility decision.',
    ],
    applications: [
      'Structured interviews',
      'Workforce development',
      'Internal mobility',
      'Mentoring',
      'Leadership programs',
    ],
  },
  {
    id: 'clinical',
    setting: 'For clinical review',
    tagline: 'Review the clinical picture alongside its supporting knowledge.',
    components: ['Noetivra', 'SKP'],
    planned: true,
    link: '/noetivra#clinical',
    linkLabel: 'Noetivra Clinical',
    paragraphs: [
      'Noetivra Clinical, a planned module, would help psychiatrists view a patient’s account, clinical history, structured assessments, and professional observations alongside approved clinical references maintained in SKP.',
      'The interface would distinguish patient reports, recorded measurements, clinician observations, and interpretations. It does not require civic judgment assessment.',
    ],
    safeguard:
      'Any diagnostic interpretation of sensor data would require clinical validation. The clinician would retain responsibility for assessment and diagnosis, and patient records would remain in controlled stores.',
    applications: [
      'Session review',
      'Clinical reference lookup',
      'Visit-to-visit comparison',
      'Note preparation for clinician approval',
    ],
  },
  {
    id: 'civil-society',
    setting: 'For civil society',
    tagline: 'Strengthen local knowledge and constructive participation.',
    components: ['SKP', 'Human Capability Framework', 'Noetivra'],
    link: '/civil-society',
    linkLabel: 'Civil society in depth',
    paragraphs: [
      'Nonprofits, community organizations, and mentorship networks often hold valuable knowledge in disconnected reports and programs. SKP can connect that knowledge and make it easier to find what has been tried, what was learned, and where support is needed.',
      'Human capability programs can focus on media literacy, independent judgment, cooperation, and practical pathways to participation. Noetivra would provide community knowledge maps and collaborative program review.',
    ],
    applications: [
      'Community knowledge hubs',
      'Misinformation resilience education',
      'Local mentoring',
      'Opportunity mapping',
    ],
  },
  {
    id: 'government',
    setting: 'For government and public institutions',
    tagline: 'Make institutional knowledge usable and decisions reviewable.',
    components: ['SKP', 'Human Capability Framework', 'Noetivra'],
    link: '/government',
    linkLabel: 'Government in depth',
    paragraphs: [
      'SKP can organize policies, program knowledge, role requirements, and approved rules so staff can find current guidance and understand how ideas and decisions connect. The Human Capability Framework can guide research and pilots in public-sector development and leadership readiness. Noetivra could provide policy exploration, staff learning, and authorized review interfaces.',
    ],
    safeguard:
      'Where a proposed application could affect a person’s access or standing, additional systems are required: validated measures, secure identity and case management, authorized human decisions, notice, independent review, appeals, and correction. SKP supplies the knowledge foundation and Noetivra the interface; neither replaces those operational and governance systems.',
    applications: [
      'Policy knowledge systems',
      'Civil-service learning',
      'Program planning',
      'Carefully evaluated civic development pilots',
    ],
  },
  {
    id: 'leadership',
    setting: 'For leadership development',
    tagline: 'Prepare people for decisions with wider consequences.',
    components: ['Human Capability Framework', 'SKP', 'Noetivra'],
    link: '/leadership',
    linkLabel: 'Leadership in depth',
    paragraphs: [
      'Leadership requires reasoning under pressure, attention to long-term effects, the ability to hear dissent, and responsibility for institutional outcomes. SyNeural’s research explores how these capabilities can be developed and assessed for specific contexts. Noetivra would support scenario exercises, decision reviews, and progress views.',
    ],
    safeguard:
      'SKP connects leadership frameworks to their definitions, research, tasks, and development activities. Any proposed qualification use would require validation for the role, an explanation of findings, and a meaningful route to review and reassessment.',
    applications: [
      'Executive education',
      'Crisis exercises',
      'Succession development',
      'Institutional leadership research',
    ],
  },
]

export const solutionCycle = [
  'Connect knowledge',
  'Define the problem',
  'Develop capability',
  'Support action',
  'Examine outcomes',
  'Improve',
]
